/**
 * Weather: how much sky is covered, where the cloud sits, and what the wind is doing.
 *
 * ## Why the presets are the aviation ones
 *
 * CLAUDE.md asks for "Clear / Few / Scattered / Broken / Overcast", and those are not arbitrary
 * labels — they are the METAR sky-condition groups, and each is a defined range of *oktas*,
 * eighths of the sky covered. Using the real definitions means the cloud layer a pilot sees
 * matches the report they would have read before departure, and it means "Broken at 4000" means
 * the same thing here as it does on an ATIS. It also gives the cloud shader a coverage number
 * with a defensible value rather than a taste-tuned one.
 *
 *   SKC/CLR  0 oktas       nothing
 *   FEW      1–2 oktas     0.19  (⅛ … 2/8, take the middle)
 *   SCT      3–4 oktas     0.44
 *   BKN      5–7 oktas     0.75
 *   OVC      8 oktas       1.00
 *
 * There is a subtlety worth stating, because it decides how the cloud shader must use this
 * number: oktas measure the fraction of the *sky dome* covered as seen from the ground, not the
 * fraction of a horizontal plane occupied by cloud. Because cloud is seen obliquely near the
 * horizon, a scattered layer covers far more of the dome than it does of the ground beneath it.
 * So the coverage value here is converted to a density threshold rather than used as an area
 * fraction — see `render/clouds/volumetricClouds.ts`.
 *
 * ## Wind
 *
 * Directions are the aviation convention: the direction the wind is coming **from**, in degrees
 * true. A 270/15 is a westerly at 15 knots. Speeds are stored in m/s because everything internal
 * is SI (CLAUDE.md), and converted at the UI boundary.
 *
 * `updateFixed` / `updateFrame`: neither. This is a state holder poked by the weather UI and
 * read by the environment; the *wind model* that turns it into per-tick gusts already exists in
 * `sim/atmosphere.ts` and runs on the physics tick.
 */

/** The METAR sky-condition groups. */
export const WEATHER_PRESETS = ['clear', 'few', 'scattered', 'broken', 'overcast'] as const;
export type WeatherPresetName = (typeof WEATHER_PRESETS)[number];

export interface WeatherState {
  preset: WeatherPresetName;
  /** Sky covered, 0…1, in the okta sense. */
  coverage: number;
  /** Cloud base [m AMSL]. */
  baseAltitudeM: number;
  /** Layer thickness [m]; the tops are `baseAltitudeM + thicknessM`. */
  thicknessM: number;
  /**
   * How lumpy the layer is, 0…1. Low is a flat stratus sheet, high is well-separated cumulus
   * with clear gaps and vertical development. Distinct from coverage: an overcast can be a
   * featureless grey lid or a solid deck of towering cumulus, and they look nothing alike.
   */
  convection: number;
  /** Surface wind direction, degrees true, the direction it blows **from**. */
  windDirectionDeg: number;
  /** Surface wind speed [m/s]. */
  windSpeedMps: number;
  /** Gust standard deviation [m/s]. */
  gustMps: number;
  /** Turbulence standard deviation [m/s]. */
  turbulenceMps: number;
  /**
   * Aerosol multiplier for the aerial perspective, 1 = clean air.
   *
   * Rises with cover because the same humid, particle-laden air that makes cloud also makes
   * haze — a genuinely overcast day has visibility measured in single-digit miles, and terrain
   * 40 km away is simply not there.
   */
  hazeMultiplier: number;
  /**
   * Precipitation intensity, 0…1. Read by the audio system for the rain-on-airframe layer.
   *
   * An **independent axis**, not a function of {@link coverage}, and that is the whole point.
   * OVC does not mean rain — the previous milestone's work was largely about making a sky
   * condition mean exactly what it says — so nothing here infers one from the other. What the
   * shipped presets do is *declare* it: everything up to Broken is dry, and the Overcast preset
   * carries a light 0.35, because a 2200 ft lid with 24 kt of wind under it is a day it is
   * plausibly raining on and this is the one preset where that reads as weather rather than as a
   * non sequitur. A future weather model can drive this from a real observation without any of
   * the presets changing meaning.
   */
  precipitation: number;
}

/**
 * The shipped presets.
 *
 * Cloud bases follow the usual relationship: base height ≈ 125 m per °C of spread between
 * temperature and dew point, which for typical conditions puts fair-weather cumulus at
 * 3000–5000 ft and lowers the deck as the air gets more saturated. Hence a Few layer sitting
 * high and dry at 5000 ft and an Overcast lid down at 2200 ft.
 *
 * The 4000 ft default on Scattered is deliberate: it is the case the milestone's acceptance
 * checklist flies through.
 */
export const WEATHER: Readonly<Record<WeatherPresetName, Readonly<WeatherState>>> = Object.freeze({
  clear: Object.freeze({
    preset: 'clear',
    coverage: 0,
    baseAltitudeM: 1_800,
    thicknessM: 600,
    convection: 0.4,
    windDirectionDeg: 250,
    windSpeedMps: 3,
    gustMps: 0.8,
    turbulenceMps: 0.35,
    hazeMultiplier: 0.8,
    precipitation: 0,
  }),
  few: Object.freeze({
    preset: 'few',
    coverage: 0.19,
    baseAltitudeM: 1_520, // 5000 ft
    thicknessM: 700,
    convection: 0.75,
    windDirectionDeg: 260,
    windSpeedMps: 4.5,
    gustMps: 1.2,
    turbulenceMps: 0.5,
    hazeMultiplier: 1.0,
    precipitation: 0,
  }),
  scattered: Object.freeze({
    preset: 'scattered',
    coverage: 0.44,
    baseAltitudeM: 1_220, // 4000 ft — the acceptance case
    thicknessM: 900,
    convection: 0.8,
    windDirectionDeg: 265,
    windSpeedMps: 7,
    gustMps: 2.2,
    turbulenceMps: 0.9,
    hazeMultiplier: 1.15,
    precipitation: 0,
  }),
  broken: Object.freeze({
    preset: 'broken',
    coverage: 0.75,
    baseAltitudeM: 1_000,
    thicknessM: 1_300,
    convection: 0.65,
    windDirectionDeg: 275,
    windSpeedMps: 10,
    gustMps: 3.4,
    turbulenceMps: 1.4,
    hazeMultiplier: 1.5,
    precipitation: 0,
  }),
  overcast: Object.freeze({
    preset: 'overcast',
    coverage: 1.0,
    baseAltitudeM: 670, // 2200 ft
    thicknessM: 1_600,
    convection: 0.25, // a flat grey lid, not towering cumulus
    windDirectionDeg: 290,
    windSpeedMps: 12,
    gustMps: 4.2,
    turbulenceMps: 2.0,
    hazeMultiplier: 2.2,
    precipitation: 0.35,
  }),
});

/** A mutable copy of a preset, for the UI to adjust. */
export function weatherFromPreset(preset: WeatherPresetName): WeatherState {
  return { ...WEATHER[preset] };
}

/**
 * Convert coverage to the wind layers `sim/atmosphere.ts` expects.
 *
 * Two things happen above the surface and both matter to how the aircraft handles:
 *
 *   - **Speed increases with height**, because the ground's friction stops mattering. The usual
 *     engineering approximation is a power law with an exponent around 1/7 over open country;
 *     that is what the 1500 m layer's factor comes from.
 *   - **Direction backs or veers.** In the northern hemisphere the surface wind is deflected
 *     *left* of the gradient wind by friction, so climbing out of the boundary layer the wind
 *     appears to veer right — the standard 20–30° over land. It is why a crosswind on the runway
 *     is not the crosswind at 2000 ft, and it is what makes a climbing turn drift.
 *
 * The sign of that veer is hemisphere dependent, so the caller passes its latitude.
 */
export function windLayersFor(
  weather: WeatherState,
  latitudeDeg: number,
): Array<{ altitudeM: number; directionDeg: number; speedMps: number }> {
  // Coriolis deflection reverses across the equator; at the equator itself there is none.
  const hemisphere = Math.sign(latitudeDeg) || 1;
  const veerDeg = 25 * hemisphere * Math.min(1, Math.abs(latitudeDeg) / 25);

  return [
    {
      altitudeM: 0,
      directionDeg: normaliseDeg(weather.windDirectionDeg),
      speedMps: weather.windSpeedMps,
    },
    {
      altitudeM: 600,
      directionDeg: normaliseDeg(weather.windDirectionDeg + veerDeg * 0.6),
      speedMps: weather.windSpeedMps * 1.45,
    },
    {
      altitudeM: 1_500,
      directionDeg: normaliseDeg(weather.windDirectionDeg + veerDeg),
      speedMps: weather.windSpeedMps * 1.9,
    },
    {
      altitudeM: 5_000,
      directionDeg: normaliseDeg(weather.windDirectionDeg + veerDeg * 1.3),
      speedMps: weather.windSpeedMps * 2.6,
    },
  ];
}

/** Is an altitude inside the cloud layer? Used for the whiteout and the in-cloud buffet. */
export function isInsideCloudLayer(weather: WeatherState, altitudeM: number): boolean {
  if (weather.coverage <= 0) return false;
  return altitudeM >= weather.baseAltitudeM && altitudeM <= weather.baseAltitudeM + weather.thicknessM;
}

/**
 * How deep into the layer an altitude is, 0 at either edge and 1 in the middle.
 *
 * Used to ramp the whiteout and the buffet rather than switching them on: cloud has ragged
 * edges, and entering one is a second or two of thickening murk, not a step change. The ramp
 * distance is capped so a thin layer still reaches full whiteout in its middle.
 */
export function cloudImmersion(weather: WeatherState, altitudeM: number): number {
  if (weather.coverage <= 0) return 0;
  const base = weather.baseAltitudeM;
  const top = base + weather.thicknessM;
  if (altitudeM <= base || altitudeM >= top) return 0;

  const rampM = Math.min(120, weather.thicknessM / 2);
  const fromBase = (altitudeM - base) / rampM;
  const fromTop = (top - altitudeM) / rampM;
  const edge = Math.min(1, fromBase, fromTop);
  // Coverage scales it: a Few layer has so much clear air between the clouds that flying
  // through the layer's altitude band is mostly not flying through cloud.
  return Math.max(0, edge) * weather.coverage;
}

function normaliseDeg(degrees: number): number {
  const wrapped = degrees % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}
